import { useTranslation } from 'react-i18next';
import { IdLabel, StateChip, TestCaseTitleLine } from '../../components/atoms.js';
import { useMyDefects } from '../../lib/my-defects.js';
import type { MyDefect } from '../../lib/runs-api.js';

// #192: „Meine Fehler zu diesem Testfall“ — die Liste unter der Ausführung zeigt nur die Defects
// des gewählten Testfalls (vorher alle des Projekts, ohne Bezug zur Auswahl) und klappt nichts
// mehr auf. Details, Verlauf und die Notiz liegen in der Seitenleiste (`MyDefectPanel`); ein Klick
// auf die Zeile öffnet sie. Eigene Lauf-Defects (#31) und selbst angelegte (#60/#61, auch ohne Lauf)
// stehen vereinigt in einer Liste (`useMyDefects`).

// Eine Zeile je Defect. Ein einziger Button statt Karte mit Unterknöpfen: die ganze Fläche ist das
// Klickziel, und Tastatur und Screenreader finden genau ein Element je Defect.
// `showTestCase` für Listen über mehrere Testfälle (Dashboard-Worklist); in der Liste zum gewählten
// Testfall wäre die Angabe in jeder Zeile dieselbe.
export function MyDefectRow({
  defect,
  onOpen,
  showTestCase,
}: {
  defect: MyDefect;
  onOpen: (defectId: string) => void;
  showTestCase: boolean;
}) {
  const { t, i18n } = useTranslation();
  // Manuell angelegte Defects haben keinen Lauf und damit kein `reported_at` — dann gilt die Anlage.
  const when = defect.reported_at ?? defect.created_at;
  const date = new Date(when).toLocaleDateString(
    i18n.resolvedLanguage === 'en' ? 'en-GB' : 'de-DE',
  );

  return (
    <button
      type="button"
      onClick={() => onOpen(defect.defect_id)}
      className="block w-full rounded-card border border-[var(--border)] bg-bg-surface p-3 text-left hover:border-brand-accent"
    >
      <span className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <IdLabel id={defect.human_id} />
          <span className="text-sm text-text-primary">{defect.title}</span>
        </span>
        <StateChip label={t(`defect.status.${defect.status}`)} />
      </span>
      {showTestCase && (
        <span className="mt-1 block">
          {/* #188: Testfall-Titel als eigene Zeile unter der Kopfzeile. */}
          <span className="block empty:hidden">
            <TestCaseTitleLine
              titleDe={defect.test_case_title_de}
              titleEn={defect.test_case_title_en}
            />
          </span>
          <span className="block text-[11px] text-text-muted">
            {defect.test_case_human_id ?? '—'}
          </span>
        </span>
      )}
      <span className="mt-1 block text-[11px] text-text-muted">
        {t('myDefects.reportedAt', { date })}
      </span>
    </button>
  );
}

export function MyDefects({
  projectId,
  testCaseId,
  onOpen,
}: {
  projectId: string;
  testCaseId: string | null;
  onOpen: (defectId: string) => void;
}) {
  const { t } = useTranslation();
  const { data } = useMyDefects(projectId);

  // Ohne gewählten Testfall gibt es nichts zu „diesem Testfall“; und kein leerer Kasten, wenn der
  // Tester dazu nichts gemeldet hat — das ist der Normalfall zu Beginn.
  if (testCaseId === null) return null;
  const rows = data.filter((d) => d.test_case_id === testCaseId);
  if (rows.length === 0) return null;

  return (
    <section className="space-y-2 px-6 pb-6">
      <h2 className="text-sm font-semibold text-text-primary">{t('myDefects.forTestCase')}</h2>
      <ul className="space-y-2">
        {rows.map((d) => (
          <li key={d.defect_id}>
            <MyDefectRow defect={d} onOpen={onOpen} showTestCase={false} />
          </li>
        ))}
      </ul>
    </section>
  );
}
